// Browser fetch helpers for /api/skills and /api/projects/:id/skills.
// Requires authenticating and passing access token retrieved via getAccessToken().

import {
    AuthRequiredError,
    GenerationApiError,
    PayloadTooLargeError
} from "./generation-api.js";

const DEFAULT_BASE_URL = readBaseUrl();

function readBaseUrl() {
    const value = import.meta.env?.VITE_API_BASE_URL;
    if (typeof value !== "string") return "";
    return value.replace(/\/+$/, "");
}

function buildHeaders(accessToken, hasBody) {
    const headers = {};
    if (hasBody) headers["content-type"] = "application/json";
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    return headers;
}

function pickError(status, body) {
    const code = typeof body?.error === "string" ? body.error : null;
    const message =
        typeof body?.message === "string" && body.message
            ? body.message
            : code || `HTTP ${status}`;

    if (status === 401) return new AuthRequiredError(message, { status, code, details: body });
    if (status === 413) return new PayloadTooLargeError(message, { status, code, details: body });
    return new GenerationApiError(message, { status, code, details: body });
}

async function jsonFetch(url, init) {
    const response = await fetch(url, init);
    let parsed = null;
    try {
        parsed = await response.json();
    } catch {
        parsed = null;
    }
    if (!response.ok) {
        throw pickError(response.status, parsed);
    }
    return parsed;
}

function rootUrl(baseUrl) {
    return typeof baseUrl === "string" ? baseUrl.replace(/\/+$/, "") : DEFAULT_BASE_URL;
}

// GET /api/skills
export async function listSkills({ accessToken, baseUrl, signal, kind } = {}) {
    const query = typeof kind === "string" && kind ? `?kind=${encodeURIComponent(kind)}` : "";
    const url = `${rootUrl(baseUrl)}/api/skills${query}`;
    return jsonFetch(url, {
        method: "GET",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

// POST /api/skills
export async function createSkill({ accessToken, name, description, content, visibility, status, baseUrl, signal } = {}) {
    if (typeof name !== "string" || !name.trim()) {
        throw new Error("createSkill requires a non-empty name");
    }
    if (typeof description !== "string") {
        throw new Error("createSkill requires description to be a string");
    }
    if (typeof content !== "string") {
        throw new Error("createSkill requires content to be a string");
    }
    const url = `${rootUrl(baseUrl)}/api/skills`;
    return jsonFetch(url, {
        method: "POST",
        headers: buildHeaders(accessToken, true),
        body: JSON.stringify({ name, description, content, visibility, status }),
        signal
    });
}

// GET /api/skills/:skillId
export async function getSkill({ accessToken, skillId, baseUrl, signal } = {}) {
    if (!skillId) {
        throw new Error("getSkill requires a skillId");
    }
    const url = `${rootUrl(baseUrl)}/api/skills/${encodeURIComponent(skillId)}`;
    return jsonFetch(url, {
        method: "GET",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

// POST /api/skills/:skillId/duplicate
export async function duplicateSkill({ accessToken, skillId, baseUrl, signal } = {}) {
    if (!skillId) {
        throw new Error("duplicateSkill requires a skillId");
    }
    const url = `${rootUrl(baseUrl)}/api/skills/${encodeURIComponent(skillId)}/duplicate`;
    return jsonFetch(url, {
        method: "POST",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

// DELETE /api/skills/:skillId
export async function deleteSkill({ accessToken, skillId, force = false, baseUrl, signal } = {}) {
    if (!skillId) {
        throw new Error("deleteSkill requires a skillId");
    }
    const forceQuery = force ? "?force=true" : "";
    const url = `${rootUrl(baseUrl)}/api/skills/${encodeURIComponent(skillId)}${forceQuery}`;
    return jsonFetch(url, {
        method: "DELETE",
        headers: buildHeaders(accessToken, false),
        signal
    });
}

// PUT /api/skills/:skillId
export async function updateSkill({
    accessToken,
    skillId,
    content,
    name,
    description,
    visibility,
    status,
    baseUrl,
    signal
} = {}) {
    if (!skillId) {
        throw new Error("updateSkill requires a skillId");
    }
    if (
        content === undefined
        && name === undefined
        && description === undefined
        && visibility === undefined
        && status === undefined
    ) {
        throw new Error("updateSkill requires at least one field to update");
    }
    if (content !== undefined && typeof content !== "string") {
        throw new Error("updateSkill requires content to be a string");
    }
    if (name !== undefined && (typeof name !== "string" || !name.trim())) {
        throw new Error("updateSkill requires name to be a non-empty string");
    }
    if (description !== undefined && typeof description !== "string") {
        throw new Error("updateSkill requires description to be a string");
    }
    const url = `${rootUrl(baseUrl)}/api/skills/${encodeURIComponent(skillId)}`;
    const body = {};
    if (content !== undefined) body.content = content;
    if (name !== undefined) body.name = name;
    if (description !== undefined) body.description = description;
    if (visibility !== undefined) body.visibility = visibility;
    if (status !== undefined) body.status = status;
    return jsonFetch(url, {
        method: "PUT",
        headers: buildHeaders(accessToken, true),
        body: JSON.stringify(body),
        signal
    });
}

// PUT /api/projects/:id/skills
export async function replaceProjectSkills({ accessToken, projectId, skillIds, baseUrl, signal } = {}) {
    if (!projectId) {
        throw new Error("replaceProjectSkills requires a projectId");
    }
    if (!Array.isArray(skillIds)) {
        throw new Error("replaceProjectSkills requires skillIds to be an array");
    }
    const url = `${rootUrl(baseUrl)}/api/projects/${encodeURIComponent(projectId)}/skills`;
    return jsonFetch(url, {
        method: "PUT",
        headers: buildHeaders(accessToken, true),
        body: JSON.stringify({ skillIds }),
        signal
    });
}

export async function loadAndRenderSkills(deps = {}) {
    const {
        refs = {},
        getAccessToken = async () => null,
        listSkillsImpl = listSkills,
        setCurrentSkills = () => {},
        renderSkillsList = () => {},
        error = () => {}
    } = deps;
    if (!refs.skillsListContainer) return false;

    refs.skillsListContainer.innerHTML = '<p class="help">読み込み中...</p>';
    if (refs.projectSkillsStatus) refs.projectSkillsStatus.textContent = "";

    try {
        let accessToken = null;
        try {
            accessToken = await getAccessToken();
        } catch {
            accessToken = null;
        }

        const data = await listSkillsImpl({ accessToken });
        setCurrentSkills(data?.skills || []);
        renderSkillsList();
        return true;
    } catch (err) {
        error("Failed to load skills", err);
        refs.skillsListContainer.innerHTML = `<p class="error" style="color:red; margin:10px 0;">Skillsの読み込みに失敗しました: ${err.message || err}</p>`;
        return false;
    }
}

export async function selectSkill(deps = {}) {
    const {
        skillId = "",
        setSelectedSkillId = () => {},
        skillItems = [],
        refs = {},
        currentSkills = [],
        getAccessToken = async () => null,
        getSkillImpl = getSkill,
        onLoaded = () => {},
        error = () => {}
    } = deps;

    setSelectedSkillId(skillId);

    skillItems.forEach((item) => {
        const id =
            item?.dataset?.skillId ||
            item?.querySelector?.(".skill-download-btn")?.dataset?.skillId ||
            item?.querySelector?.(".skill-checkbox")?.dataset?.skillId ||
            "";
        item.classList.toggle("is-selected", id === skillId);
    });

    if (!refs.skillsEditorTitle) return false;

    const skill = currentSkills.find((entry) => entry.id === skillId);
    if (!skill) return false;

    refs.skillsEditorTitle.textContent = `SKILL.md エディタ - ${skill.name}`;

    if (!refs.skillContentEditor) return true;

    refs.skillContentEditor.value = "本文を読み込み中...";
    try {
        let accessToken = null;
        try {
            accessToken = await getAccessToken();
        } catch {
            accessToken = null;
        }

        const detail = await getSkillImpl({ accessToken, skillId });
        if (refs.skillNameInput) refs.skillNameInput.value = detail?.name || skill.name || "";
        if (refs.skillDescriptionInput) refs.skillDescriptionInput.value = detail?.description || "";
        if (refs.skillStatusSelect) refs.skillStatusSelect.value = detail?.status || skill.status || "draft";
        if (refs.skillVisibilityNote) {
            const visibility = detail?.visibility || skill.visibility || "private";
            refs.skillVisibilityNote.textContent = visibility === "shared"
                ? "公開範囲: shared"
                : "公開範囲: private";
        }
        refs.skillContentEditor.value = detail?.content || "";
        onLoaded(detail);
        return true;
    } catch (err) {
        error("Failed to load skill content", err);
        refs.skillContentEditor.value = `本文の取得に失敗しました: ${err.message || err}`;
        return false;
    }
}

export async function saveProjectSkills(deps = {}) {
    const {
        refs = {},
        projectId = "",
        selectedSkillIds = [],
        getAccessToken = async () => null,
        replaceProjectSkillsImpl = replaceProjectSkills,
        applySavedSkillIds = () => {},
        syncAssistantState = () => {},
        setTimeoutFn = () => {},
        error = () => {}
    } = deps;

    if (!projectId) {
        if (refs.projectSkillsStatus) {
            refs.projectSkillsStatus.textContent = "プロジェクトがロードされていません（ローカル保存時は適用できません）";
            refs.projectSkillsStatus.style.color = "red";
        }
        return false;
    }

    if (refs.projectSkillsStatus) {
        refs.projectSkillsStatus.textContent = "ダウンロードして有効化中...";
        refs.projectSkillsStatus.style.color = "";
    }

    try {
        let accessToken = null;
        try {
            accessToken = await getAccessToken();
        } catch {
            accessToken = null;
        }

        const result = await replaceProjectSkillsImpl({
            accessToken,
            projectId,
            skillIds: selectedSkillIds
        });

        const savedSkillIds = result?.skillIds || [];
        applySavedSkillIds(savedSkillIds);
        syncAssistantState(savedSkillIds);

        if (refs.projectSkillsStatus) {
            refs.projectSkillsStatus.textContent = "ダウンロードして有効化しました！";
            refs.projectSkillsStatus.style.color = "green";
            setTimeoutFn(() => {
                if (refs.projectSkillsStatus.textContent === "ダウンロードして有効化しました！") {
                    refs.projectSkillsStatus.textContent = "";
                }
            }, 3000);
        }
        return true;
    } catch (err) {
        error("Failed to save project skills", err);
        if (refs.projectSkillsStatus) {
            refs.projectSkillsStatus.textContent = `ダウンロードに失敗しました: ${err.message || err}`;
            refs.projectSkillsStatus.style.color = "red";
        }
        return false;
    }
}

export async function saveSkillContent(deps = {}) {
    const {
        refs = {},
        skillId = "",
        content = "",
        name = "",
        description = "",
        status = "draft",
        getAccessToken = async () => null,
        updateSkillImpl = updateSkill,
        onSaved = () => {},
        error = () => {}
    } = deps;

    if (!skillId) {
        if (refs.skillEditorStatus) {
            refs.skillEditorStatus.textContent = "保存するSkillを選択してください。";
            refs.skillEditorStatus.style.color = "red";
        }
        return false;
    }

    if (refs.skillEditorStatus) {
        refs.skillEditorStatus.textContent = "保存中...";
        refs.skillEditorStatus.style.color = "";
    }

    try {
        let accessToken = null;
        try {
            accessToken = await getAccessToken();
        } catch {
            accessToken = null;
        }

        const saved = await updateSkillImpl({
            accessToken,
            skillId,
            content,
            name,
            description,
            status
        });
        onSaved(saved);
        if (refs.skillEditorStatus) {
            refs.skillEditorStatus.textContent = "ワークスペースに保存しました。";
            refs.skillEditorStatus.style.color = "green";
        }
        return true;
    } catch (err) {
        error("Failed to save skill content", err);
        if (refs.skillEditorStatus) {
            refs.skillEditorStatus.textContent = `Skillの保存に失敗しました: ${err.message || err}`;
            refs.skillEditorStatus.style.color = "red";
        }
        return false;
    }
}
